import React, { BaseSyntheticEvent, useRef, useState } from 'react';
import useAuth from 'src/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { VerificationInfo, VerificationStatus } from 'src/shared/types';
import PhoneVerification from 'pages/Personal/components/PhoneVerification/PhoneVerification';
import CheckIcon from 'components/CheckIcon/CheckIcon';

const Info: React.FC<any> = (): React.ReactElement => {
	const { getUser, verifyPhone, sendSMS, updatePhoneNumber, refreshUser, emailVerificationToken } = useAuth();
	const [user, setUser] = useState(getUser);
	const [verificationCode, setVerificationCode] = useState<string>('');
	const [verifiedPhone, setVerifiedPhone] = useState<boolean>(user.phone!.status === VerificationStatus.VERIFIED);
	const [verifiedEmail, _setVerifiedEmail] = useState<boolean>(user.email!.status === VerificationStatus.VERIFIED);
	const [message, setMessage] = useState<string>('');
	const formRef = useRef() as React.MutableRefObject<HTMLFormElement>;
	const [verifyPhoneMode, setVerifyPhoneMode] = useState<boolean>(false);
	const { t } = useTranslation();
	const [isSMSSent, setIsSMSSent] = useState<boolean>(false);
	const [isPhoneUpdated, setIsPhoneUpdated] = useState<boolean>(true);
	const [phoneNumber, setPhoneNumber] = useState<string>(user.phone!.value);
	const [verifyEmailSent, setVerifyEmailSent] = useState<boolean>(false);

	/*const updateUserInfo = (e: BaseSyntheticEvent) => {
		e.preventDefault();
		e.stopPropagation();
		updateUser(user).then(() => {
			refreshUser().then();
		});
	};*/

	const handlePhoneVerification = async (event: BaseSyntheticEvent) => {
		event.preventDefault();
		event.stopPropagation();
		const verified = await verifyPhone(verificationCode);
		if (verified === true) {
			setVerifiedPhone(true);
			setVerifyPhoneMode(false);
		} else {
			setMessage(verified.message);
		}
	};

	const handleCodeInput = (event: BaseSyntheticEvent) => {
		setVerificationCode(event.target.value);
		setMessage('');
	};

	const handleEmailVerification = (e: BaseSyntheticEvent) => {
		e.preventDefault();
		e.stopPropagation();
		emailVerificationToken().then((r) => {
			if (r) {
				setVerifyEmailSent(true);
			}
		});
	};

	const handleSendSMS = async (event: BaseSyntheticEvent) => {
		event.preventDefault();
		event.stopPropagation();
		const smsSent = await sendSMS();
		if (smsSent) {
			setIsSMSSent(true);
		}
	};

	const handleChangePhoneNumber = (event: BaseSyntheticEvent) => {
		setPhoneNumber(event.target.value);
		const user = getUser();
		if (user?.phone!.value !== event.target.value) {
			setIsPhoneUpdated(false);
		} else {
			setIsPhoneUpdated(true);
		}
	};

	const handleUpdatePhoneNumber = async () => {
		if (phoneNumber) {
			const result = await updatePhoneNumber(phoneNumber);
			await refreshUser();
			const user = getUser();
			if (user?.phone!.status === VerificationStatus.VERIFIED) {
				setVerifiedPhone(true);
			}
			if (result) {
				setIsPhoneUpdated(true);
			}
		}
	};

	return user ? <div className={'personal-info-container'}>
		{!verifyPhoneMode && <form ref={formRef} className={'personal-info'}>
			<div className="input-container">
				<label className={'big'}
							 htmlFor={'firstname'}>{t('common.labels.firstName')}</label>
				<input
					name={'firstname'}
					id={'firstname'}
					type="text"
					value={user.firstName || ''}
					onInput={(e: BaseSyntheticEvent) => {
						setUser({
							...user,
							firstName: e.target.value,
						});
					}}
					required
				/>
			</div>
			<div className="input-container">
				<label className={'big'}
							 htmlFor={'lastname'}>{t('common.labels.lastName')}</label>
				<input
					name={'lastname'}
					id={'lastname'}
					type="text"
					value={user.lastName || ''}
					required
					onInput={(e: BaseSyntheticEvent) => {
						setUser({
							...user,
							lastName: e.target.value,
						});
					}}
				/>
			</div>
			<div className="input-container">
				<label className={'big'}
							 htmlFor={'phone'}>{t('common.labels.phone')}</label>
				<input
					name={'phone'}
					id={'phone'}
					type="text"
					value={user.phone?.value || ''}
					required
					onInput={(e: BaseSyntheticEvent) => {
						setUser({
							...user,
							phone: {
								status: user.phone?.status,
								value: e.target.value
							} as VerificationInfo
						});
					}}
				/>
				{!verifiedPhone ? <button className="verify" onClick={(e: BaseSyntheticEvent) => {
					e.preventDefault();
					e.stopPropagation();
					setVerifyPhoneMode((mode) => !mode);
				}}>
					{t('pages.personal.phoneVerification.verify')}
				</button> : (
					<div className="verify">
						<CheckIcon width={10} color={'#038746'}/>{t('pages.personal.phoneVerification.verified')}
					</div>
				)}
			</div>
			<div className="input-container">
				<label className={'big'}
							 htmlFor={'email'}>{t('common.labels.email')}</label>
				<input
					name={'email'}
					id={'email'}
					type="text"
					value={user.email?.value || ''}
					required
					onInput={(e: BaseSyntheticEvent) => {
						setUser({
							...user,
							email: {
								status: user.email?.status,
								value: e.target.value
							} as VerificationInfo
						});
					}}
				/>
				{!verifiedEmail ?
					verifyEmailSent ? (
							<div className="verify sent">
								{t('pages.forgotPassword.emailSent')}
							</div>
						) :
						<button className="verify" onClick={handleEmailVerification}>
							{t('pages.personal.phoneVerification.verify')}
						</button> : (
						<div className="verify">
							<CheckIcon width={10} color={'#038746'}/>{t('pages.personal.phoneVerification.verified')}
						</div>
					)}
			</div>
			{/*<div className="input-container">
			<label
				htmlFor={'newPassword'}>{t('common.labels.newPassword')}</label>
			<input
				name={'newPassword'}
				id={'newPassword'}
				type="password"
				value={user.password || ''}
				required
				onInput={(e: BaseSyntheticEvent) => {
					setUser({
						...user,
						password: e.target.value,
					});
				}}
			/>
		</div>
		<div className="input-container">
			<label
				htmlFor={'passwordConfirmation'}>{t('common.labels.passwordConfirmation')}</label>
			<input
				name={'passwordConfirmation'}
				id={'passwordConfirmation'}
				type="text"
				value={user.passwordConfirmation || ''}
				required
				onInput={(e: BaseSyntheticEvent) => {
					setUser({
						...user,
						passwordConfirmation: e.target.value,
					});
				}}
			/>
		</div>*/}
			{/*<div className="button-container">
			<button
				className={'scroll-to-button'}
				onClick={updateUserInfo}
			>
				{t('common.continue')}
			</button>
		</div>*/}

		</form>}
		{verifyPhoneMode && <PhoneVerification
			isSMSSent={isSMSSent}
			handleSendSMS={handleSendSMS}
			handleCodeInput={handleCodeInput}
			handlePhoneVerification={handlePhoneVerification}
			phoneNumber={phoneNumber}
			handleChangePhoneNumber={handleChangePhoneNumber}
			isPhoneUpdated={isPhoneUpdated}
			handleUpdatePhoneNumber={handleUpdatePhoneNumber}
			message={message}
		/>}
	</div> : <></>;
};

export default Info;
